/**
 * Hero Component - HI-SI Landing Page
 * HISIP-016: Landing + Demo (Brand Site for HI‑SI)
 */

import { motion } from 'framer-motion';
import { Brain, Zap, ArrowRight, Play } from 'lucide-react';
import { cn } from '@/utils/cn';

interface HeroProps {
  onStartCreating: () => void;
  onWatchDemo: () => void;
  onSeePricing: () => void;
}

export default function Hero({ onStartCreating, onWatchDemo, onSeePricing }: HeroProps) {
  return (
    <div className="relative overflow-hidden">
      {/* Hero Background Effects */}
      <div className="absolute inset-0">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/50 via-black to-cyan-900/30" />
        
        {/* Animated grid */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(168,85,247,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(168,85,247,0.1)_1px,transparent_1px)] bg-[size:50px_50px]" />
        </div>
        
        {/* Floating orbs */}
        <motion.div
          className="absolute top-20 left-20 w-32 h-32 rounded-full bg-purple-500/10 blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute top-40 right-32 w-48 h-48 rounded-full bg-cyan-500/10 blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
      </div>

      {/* Hero Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        <div className="text-center">
          
          {/* Brand Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/30 backdrop-blur-xl border border-purple-500/20">
              <Brain className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-mono text-white/80 uppercase tracking-wider">
                High Signal Intelligence
              </span>
            </div>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-light text-white mb-6"
          >
            <span className="block">
              <span className="bg-gradient-to-r from-purple-400 via-purple-300 to-cyan-300 bg-clip-text text-transparent">
                Hi-Si.ai
              </span>
            </span>
            <span className="block text-3xl sm:text-4xl lg:text-5xl mt-2 text-white/90">
              From Imagination to Media
            </span>
          </motion.h1>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl sm:text-2xl text-white/70 mb-4 max-w-3xl mx-auto leading-relaxed"
          >
            Transform ideas into professional videos through our eleven‑stage AI pipeline
          </motion.p>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-lg text-white/50 mb-12 max-w-2xl mx-auto"
          >
            From script to screen in minutes, not hours. Professional quality, powered by specialized agentic personas and your choice of the most powerful models. All managed by your personal Agent.
          </motion.p>

          {/* Call-to-Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
          >
            {/* Primary CTA */}
            <motion.button
              onClick={onStartCreating}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={cn(
                "group relative px-8 py-4 rounded-xl font-medium text-lg",
                "bg-gradient-to-r from-purple-600 to-cyan-600",
                "text-white shadow-xl",
                "hover:shadow-2xl hover:shadow-purple-500/25",
                "transition-all duration-300",
                "flex items-center gap-3"
              )}
              style={{
                boxShadow: '0 0 40px rgba(168, 85, 247, 0.3)'
              }}
            >
              <Zap className="w-5 h-5 group-hover:animate-pulse" />
              Start Creating
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              
              {/* Button glow effect */}
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 opacity-0 group-hover:opacity-20 blur-xl transition-opacity" />
            </motion.button>

            {/* Secondary CTA */}
            <motion.button
              onClick={onWatchDemo}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={cn(
                "group px-8 py-4 rounded-xl font-medium text-lg",
                "bg-black/30 backdrop-blur-xl border border-white/20",
                "text-white hover:bg-white/10",
                "transition-all duration-300",
                "flex items-center gap-3"
              )}
            >
              <Play className="w-5 h-5" />
              Watch Demo
            </motion.button>
          </motion.div>

          {/* Key Features Highlight */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto"
          >
            {[
              {
                icon: Brain,
                title: "AI-Powered Pipeline",
                description: "Seven specialized AI stages work together"
              },
              {
                icon: Zap,
                title: "Lightning Fast",
                description: "Professional videos in minutes, not hours"
              },
              {
                icon: ArrowRight,
                title: "Iterative Quality",
                description: "Each stage refines until perfection"
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-500/10 border border-purple-500/20 mb-3">
                  <feature.icon className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-white font-medium mb-2">{feature.title}</h3>
                <p className="text-white/60 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Pricing Link */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-12"
          >
            <button
              onClick={onSeePricing}
              className="text-cyan-400 hover:text-cyan-300 text-sm font-mono uppercase tracking-wider transition-colors"
            >
              See Pricing →
            </button>
          </motion.div>

        </div>
      </div>
    </div>
  );
}